import { transition } from '@helpers/animate';
import brand, { ThemeOption } from '@helpers/brand';
import { Device, between, from, until } from '@helpers/media';
import { h3, paragraphSmall } from '@helpers/typography';
import { CtaCardsStyles } from '@stories/Widgets/CtaCards/CtaCards.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.a `
  display: block;
  color: inherit;
  text-decoration: none;
  margin-top: 40px;

  &:first-child {
    margin-top: 0;
  }

  @media ${from(Device.MobileLarge)} {
    margin-top: 0;

    &:first-child {
      ${CtaCardsStyles.Container}[data-featured=true] & {
        grid-column: span 2;
        display: grid;
        grid-template-columns: 48% 1fr;
        grid-column-gap: 0;
        border-top: 1px solid ${rgba(brand.orange, 0.6)};

        &[data-component-theme=${ThemeOption.Orange}] {
          border-top-color: ${rgba(brand.orange, 0.6)};
        }

        &[data-component-theme=${ThemeOption.Purple}] {
          border-top-color: ${rgba(brand.purple, 0.6)};
        }

        &[data-component-theme=${ThemeOption.Blue}] {
          border-top-color: ${rgba(brand.blue, 0.6)};
        }

        &[data-component-theme=${ThemeOption.Red}] {
          border-top-color: ${rgba(brand.red, 0.6)};
        }

        &[data-component-theme=${ThemeOption.Green}] {
          border-top-color: ${rgba(brand.green.default, 0.6)};
        }

        &[data-component-theme=${ThemeOption.DarkGreen}] {
          border-top-color: ${rgba(brand.green.dark, 0.6)};
        }

        &[data-component-theme=${ThemeOption.Teal}] {
          border-top-color: ${rgba(brand.teal, 0.6)};
        }
      }
    }
  }

  @media ${between(Device.Tablet, Device.TabletLarge)} {
    &:first-child {
      ${CtaCardsStyles.Container}[data-featured=true] & {
        grid-column: span 3;
      }
    }
  }
`;
const ImageWrapper = styled.div `
  position: relative;
  overflow: hidden;
`;
const Image = styled.img `
  height: auto;
  ${transition('transform')};
  transform-origin: 50% 50%;

  @media ${from(Device.MobileLarge)} {
    ${Container}:first-child & {
      ${CtaCardsStyles.Container}[data-featured=true] & {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }
    }
  }

  @media ${until(Device.MobileLarge)} {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  ${Container}:hover & {
    transform: scale(1.1);
  }
`;
const ContentWrapper = styled.div `
  position: relative;
  padding: 20px 6px 20px;
  border-bottom: 1px solid ${rgba(brand.orange, 0.6)};

  ${Container}[data-component-theme=${ThemeOption.Orange}] & {
    --themeColor: ${brand.orange};
    border-bottom-color: ${rgba(brand.orange, 0.6)};
    border-top-color: ${rgba(brand.orange, 0.6)};
  }

  ${Container}[data-component-theme=${ThemeOption.Purple}] & {
    --themeColor: ${brand.purple};
    border-bottom-color: ${rgba(brand.purple, 0.6)};
    border-top-color: ${rgba(brand.purple, 0.6)};
  }

  ${Container}[data-component-theme=${ThemeOption.Blue}] & {
    --themeColor: ${brand.blue};
    border-bottom-color: ${rgba(brand.blue, 0.6)};
    border-top-color: ${rgba(brand.blue, 0.6)};
  }

  ${Container}[data-component-theme=${ThemeOption.Red}] & {
    --themeColor: ${brand.red};
    border-bottom-color: ${rgba(brand.red, 0.6)};
    border-top-color: ${rgba(brand.red, 0.6)};
  }

  ${Container}[data-component-theme=${ThemeOption.Green}] & {
    --themeColor: ${brand.green.default};
    border-bottom-color: ${rgba(brand.green.default, 0.6)};
    border-top-color: ${rgba(brand.green.default, 0.6)};
  }

  ${Container}[data-component-theme=${ThemeOption.DarkGreen}] & {
    --themeColor: ${brand.green.dark};
    border-bottom-color: ${rgba(brand.green.dark, 0.6)};
    border-top-color: ${rgba(brand.green.dark, 0.6)};
  }

  ${Container}[data-component-theme=${ThemeOption.Teal}] & {
    --themeColor: ${brand.teal};
    border-bottom-color: ${rgba(brand.teal, 0.6)};
    border-top-color: ${rgba(brand.teal, 0.6)};
  }

  @media ${from(Device.MobileLarge)} {
    padding: 31px 6px 30px;

    ${Container}:first-child & {
      ${CtaCardsStyles.Container}[data-featured=true] & {
        padding: 31px 60px 60px 36px;
      }
    }
  }
`;
const Title = styled.h2 `
  ${h3};
  margin-bottom: 0;
  padding-right: 60px;
  color: var(--themeColor);

  @media ${from(Device.MobileLarge)} {
    ${Container}:first-child & {
      ${CtaCardsStyles.Container}[data-featured=true] & {
        padding-right: 0;
      }
    }
  }
`;
const ArrowIcon = styled.div `
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 13px;
  right: 6px;
  width: 40px;
  height: 40px;
  border-radius: 100%;
  background-color: var(--themeColor);

  @media ${from(Device.MobileLarge)} {
    top: 26px;

    ${Container}:first-child & {
      ${CtaCardsStyles.Container}[data-featured=true] & {
        top: auto;
        bottom: 24px;
      }
    }
  }

  svg {
    width: 26px;
    height: 26px;
    color: ${brand.white};

    path {
      ${transition('stroke-width')}

      ${Container}:hover & {
        stroke-width: 5;
      }
    }
  }
`;
const Content = styled.p `
  ${paragraphSmall};
  font-size: 18px;
  margin: 1.5em auto 0;

  ${CtaCardsStyles.Container}[data-hide-descriptions=true] & {
    display: none;
  }

  ${Container}:first-child & {
    ${CtaCardsStyles.Container}[data-featured=true][data-hide-descriptions=true] & {
      display: block;
    }
  }
`;
export const CtaCardStyles = {
    ArrowIcon,
    Container,
    Content,
    ContentWrapper,
    Image,
    ImageWrapper,
    Title,
};
